import { useTranslation } from "react-i18next";
import {
	DeleteOutlined,
	EditOutlined,
	MoreOutlined,
	PushpinOutlined
} from "@ant-design/icons";
import { Button, Dropdown, Image, Input, Menu, message, Modal } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DislikeActiveIcon, DislikeIcon, FeedbackIcon, LikeActiveIcon, LikeIcon } from "../../../../../../../components/course-construction/content-course-construction/statistic-course/components/icons";
import UploadToDiscuss from "../../../../../../../components/file-uploader/UploadToDiscuss";
import { DISLIKE_REACTION, LIKE_REACTION } from "../../../../../../../constants";
import "./CommentSection.scss";
import { CommentNode, createCommentTree } from "./commentTree";
import { getFileIcon, RawComment } from "./commentTypes";
import ReplyForm from "./ReplyForm";

dayjs.extend(relativeTime);

export interface MenuOption {
	key: string;
	label: string;
	icon?: ReactNode;
	danger?: boolean;
	onClick: (comment: CommentNode) => void;
}

interface CommentSectionProps {
	discussionId: number;
	root: RawComment;
	replies: RawComment[];
	isPinned?: boolean;
	isSaved?: boolean;
	menuOptions?: (comment: CommentNode) => MenuOption[];
	onEdit?: (id: number, content: string, attachments?: any) => Promise<void>;
	onDelete?: (node: CommentNode) => Promise<void>;
	onSubmitReply?: (data: {
		discussionId: number;
		parentId: number;
		replyToId: number;
		level: number;
		content: string;
		attachments?: File[];
	}) => Promise<void>;
	onReact?: (commentId: number, isLike: boolean) => Promise<void>;
	newReplyIds?: Set<number>;
	onNodeExpanded?: (nodeId: number) => void;
	onSeenDiscuss: (id: number) => Promise<void>;
}

interface ReplyContext {
	parentId: number;
	mention: string;
	replyToId: number;
	uid: number;
}

export default function CommentSection({
	discussionId,
	root,
	replies,
	isPinned,
	isSaved,
	menuOptions,
	onEdit,
	onDelete,
	onSubmitReply,
	onReact,
	newReplyIds,
	onNodeExpanded,
	onSeenDiscuss
}: CommentSectionProps) {
  const { t } = useTranslation();
	const tree = useMemo(() => createCommentTree(root, replies), [root, replies]);
	const [activeReply, setActiveReply] = useState<ReplyContext | null>(null);
	const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
	const [editingId, setEditingId] = useState<number | null>(null);
	const [localNewReplyIds, setLocalNewReplyIds] = useState<Set<number>>(newReplyIds ?? new Set());

	const toggleExpanded = (nodeId: number) => {
		setExpandedIds(prev => {
			const next = new Set(prev);
			if (next.has(nodeId)) {
				next.delete(nodeId);

				const node = findNodeById(tree, nodeId);
				if (node) {
					const idsToRemove = node.children.map(child => child.id);
					setLocalNewReplyIds(prevIds => {
						const updated = new Set(prevIds);
						idsToRemove.forEach(id => updated.delete(id));
						return updated;
					});
				}

			} else {
				next.add(nodeId);
				onNodeExpanded?.(nodeId);
			}
			return next;
		});
	};

	const handleReply = useCallback((node: CommentNode) => {
		const parentId = node.level === 3 ? node.parentId! : node.id;
		setActiveReply({
			parentId,
			mention: `@${node.author.username} `,
			replyToId: node.id,
			uid: Date.now(),
		});
	}, []);

	const handleSubmitReply = useCallback(async ({ replyToId, content, attachments }: {
		replyToId: number;
		content: string;
		attachments?: File[];
	}) => {
		if (!onSubmitReply || !activeReply) return;

		try {
			await onSubmitReply({
				discussionId: tree.discussionId,
				parentId: activeReply.parentId,
				replyToId,
				level: Math.min(tree.level + 1, 3),
				content,
				attachments,
			});

			const parentNode = findNodeById(tree, activeReply.parentId);
			if (parentNode && parentNode.children.length === 0) {
				setExpandedIds(prev => {
					const next = new Set(prev);
					next.add(activeReply.parentId);
					return next;
				});
			}

			setActiveReply(null);
			message.success(t("Đã gửi phản hồi", "Đã gửi phản hồi"));
		} catch (error) {
			message.error(t("Không thể gửi phản hồi", "Không thể gửi phản hồi"));
		}
	}, [activeReply, onSubmitReply, tree]);

	return (
		<div className="cmt-section">
			<CommentThread
				node={tree}
				depth={0}
				discussionId={discussionId}
				isPinned={isPinned}
				isSaved={isSaved}
				activeReply={activeReply}
				setActiveReply={setActiveReply}
				expandedIds={expandedIds}
				toggleExpanded={toggleExpanded}
				menuOptions={menuOptions}
				onEdit={onEdit}
				onDelete={onDelete}
				editingId={editingId}
				setEditingId={setEditingId}
				onReply={handleReply}
				onSubmitReply={handleSubmitReply}
				onReact={onReact}
				newReplyIds={localNewReplyIds}
				onSeenDiscuss={onSeenDiscuss}
			/>
		</div>
	);
}

function findNodeById(node: CommentNode, id: number): CommentNode | null {
	if (node.id === id) return node;
	for (const child of node.children) {
		const found = findNodeById(child, id);
		if (found) return found;
	}
	return null;
}

interface CommentThreadProps {
	node: CommentNode;
	depth: number;
	discussionId: number;
	isPinned?: boolean;
	isSaved?: boolean;
	activeReply: ReplyContext | null;
	setActiveReply: Dispatch<SetStateAction<ReplyContext | null>>;
	expandedIds: Set<number>;
	toggleExpanded: (nodeId: number) => void;
	menuOptions?: (comment: CommentNode) => MenuOption[];
	onEdit?: (id: number, content: string, attachments?: any) => Promise<void>;
	onDelete?: (node: CommentNode) => Promise<void>;
	editingId: number | null;
	setEditingId: Dispatch<SetStateAction<number | null>>;
	onReply: (node: CommentNode) => void;
	onSubmitReply: (data: { replyToId: number; content: string; attachments?: File[] }) => void;
	onReact?: (commentId: number, isLike: boolean) => void;
	newReplyIds?: Set<number>;
	onSeenDiscuss: (id: number) => Promise<void>;
}

function CommentThread({
	node,
	depth,
	discussionId,
	isPinned,
	isSaved,
	activeReply,
	setActiveReply,
	expandedIds,
	toggleExpanded,
	menuOptions,
	onEdit,
	onDelete,
	editingId,
	setEditingId,
	onReply,
	onSubmitReply,
	onReact,
	newReplyIds,
	onSeenDiscuss
}: CommentThreadProps) {
  const { t } = useTranslation();
	const isExpanded = expandedIds.has(node.id);
	const hasReplies = node.children.length > 0;
	const showReplyForm = activeReply?.parentId === node.id;

	const { oldReplies, newRepliesWhenCollapsed } = useMemo(() => {
		if (!hasReplies) return { oldReplies: [], newRepliesWhenCollapsed: [] };

		if (!newReplyIds || isExpanded) {
			return { oldReplies: node.children, newRepliesWhenCollapsed: [] };
		}

		const old: CommentNode[] = [];
		const newWhenCollapsed: CommentNode[] = [];

		node.children.forEach(child => {
			if (newReplyIds.has(child.id)) {
				newWhenCollapsed.push(child);
			} else {
				old.push(child);
			}
		});

		return { oldReplies: old, newRepliesWhenCollapsed: newWhenCollapsed };
	}, [node.children, newReplyIds, isExpanded, hasReplies]);

	const hasOldReplies = oldReplies.length > 0;
	const hasNewReplies = newRepliesWhenCollapsed.length > 0;
	const hasChildrenContent = (hasReplies && isExpanded) || showReplyForm || (!isExpanded && (hasOldReplies || hasNewReplies));
	const containerRef = useRef<any>(null);

	useEffect(() => {
		if (showReplyForm && containerRef.current) {
			const replyWrapper = containerRef.current.querySelector('.reply-wrapper');
			if (replyWrapper) {
				const parentHeight = containerRef.current.offsetHeight;
				(replyWrapper as HTMLElement).style.setProperty('--line-height', `${parentHeight}px`);
			}
		}
	}, [showReplyForm, isExpanded, node.children.length]);

	useEffect(() => {
		if (!containerRef.current) return;

		const updateToggleLines = () => {
			const container = containerRef.current;
			const avatar = container.querySelector('.cmt-avatar');
			const toggleWrapper = container.querySelector(`.toggle-wrapper[data-parent-id="${node.id}"]`);

			if (avatar && toggleWrapper) {
				const containerRect = container.getBoundingClientRect();
				const avatarRect = avatar.getBoundingClientRect();
				const toggleRect = toggleWrapper.getBoundingClientRect();

				const avatarCenter = avatarRect.top - containerRect.top + (avatarRect.height / 2);
				const toggleTop = toggleRect.top - containerRect.top;

				const lineHeight = Math.abs(toggleTop - avatarCenter);

				toggleWrapper.style.setProperty('--connect-height', `${lineHeight}px`);
			}
		};

		requestAnimationFrame(updateToggleLines);

		const observer = new ResizeObserver(updateToggleLines);
		observer.observe(containerRef.current);

		return () => observer.disconnect();
	}, [node.id, hasReplies, isExpanded, node.children.length, depth]);

	return (
		<div
			ref={containerRef}
			className={`parent-container depth-${depth} ${hasChildrenContent ? 'has-line' : ''} ${showReplyForm ? 'has-active-reply' : ''}`}>
			<CommentItem
				node={node}
				discussionId={discussionId}
				isPinned={depth === 0 ? isPinned : undefined}
				isSaved={depth === 0 ? isSaved : undefined}
				onReply={() => onReply(node)}
				menuOptions={menuOptions}
				onEdit={onEdit}
				onDelete={onDelete}
				isEditing={editingId === node.id}
				onStartEdit={() => setEditingId(node.id)}
				onCancelEdit={() => setEditingId(null)}
				onReact={onReact}
			/>

			{hasChildrenContent && (
				<div className="children-container">
					{!isExpanded && hasOldReplies && (
						<div
							className={`toggle-wrapper depth-${depth + 1}`}
							data-parent-id={node.id}
						>
							<button
								className="toggle-replies"
								onClick={() => {
									toggleExpanded(node.id)
									onSeenDiscuss && onSeenDiscuss(node.discussionId)

								}}
							>
								{`Xem ${oldReplies.length} phản hồi`}
							</button>
						</div>
					)}

					{!isExpanded && hasNewReplies && (
						<div className="children-list newest-replies">
							{newRepliesWhenCollapsed.map((child) => (
								<CommentThread
									key={child.id}
									node={child}
									depth={depth + 1}
									discussionId={discussionId}
									activeReply={activeReply}
									setActiveReply={setActiveReply}
									expandedIds={expandedIds}
									toggleExpanded={toggleExpanded}
									menuOptions={menuOptions}
									onEdit={onEdit}
									onDelete={onDelete}
									editingId={editingId}
									setEditingId={setEditingId}
									onReply={onReply}
									onSubmitReply={onSubmitReply}
									onReact={onReact}
									newReplyIds={newReplyIds}
									onSeenDiscuss={onSeenDiscuss}
								/>
							))}
						</div>
					)}

					{isExpanded && hasReplies && (
						<div className="children-list">
							{node.children.map((child) => (
								<CommentThread
									key={child.id}
									node={child}
									depth={depth + 1}
									discussionId={discussionId}
									activeReply={activeReply}
									setActiveReply={setActiveReply}
									expandedIds={expandedIds}
									toggleExpanded={toggleExpanded}
									menuOptions={menuOptions}
									onEdit={onEdit}
									onDelete={onDelete}
									editingId={editingId}
									setEditingId={setEditingId}
									onReply={onReply}
									onSubmitReply={onSubmitReply}
									onReact={onReact}
									newReplyIds={newReplyIds}
									onSeenDiscuss={onSeenDiscuss}

								/>
							))}

							<div className={`toggle-wrapper depth-${depth + 1}`} data-parent-id={node.id}>
								<button
									className="toggle-replies"
									onClick={() => toggleExpanded(node.id)}
								>
									{t("Ẩn phản hồi", "Ẩn phản hồi")}
								</button>
							</div>
						</div>
					)}

					{showReplyForm && activeReply && (
						<div className={`reply-wrapper depth-${Math.min(depth + 1, 3)}`} data-parent={node.id}>
							<ReplyForm
								mention={activeReply.mention}
								replyToId={activeReply.replyToId}
								uid={activeReply.uid}
								onCancel={() => setActiveReply(null)}
								onSubmit={onSubmitReply}
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);
}

interface CommentItemProps {
	node: CommentNode;
	discussionId: number;
	isPinned?: boolean;
	isSaved?: boolean;
	onReply: () => void;
	menuOptions?: (node: CommentNode) => MenuOption[];
	onEdit?: (id: number, content: string, attachments?: any) => Promise<void>;
	onDelete?: (node: CommentNode) => Promise<void>;
	isEditing: boolean;
	onStartEdit: () => void;
	onCancelEdit: () => void;
	onReact?: (commentId: number, isLike: boolean) => void;
}

function CommentItem({
	node,
	discussionId,
	isPinned,
	isSaved,
	onReply,
	menuOptions,
	onEdit,
	onDelete,
	isEditing,
	onStartEdit,
	onCancelEdit,
	onReact
}: CommentItemProps) {
  const { t } = useTranslation();
	const [editContent, setEditContent] = useState(node.content);
	const [loading, setLoading] = useState(false);
	const [fileList, setFileList] = useState<any[]>([]);
	const [addAttachments, setAddAttachments] = useState<any[]>([]);
	const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([]);

	const isDiscussion = node.level === 1;
	const entityType = isDiscussion ? 'thảo luận' : 'phản hồi';

	const likeInfo = useMemo(() => {
		const likeReaction = node.reactionCount?.find(r => r.reactionType === LIKE_REACTION);
		const dislikeReaction = node.reactionCount?.find(r => r.reactionType === DISLIKE_REACTION);
		const isLiked = node.myReaction === LIKE_REACTION;
		const isDisliked = node.myReaction === DISLIKE_REACTION;
		const likeCount = likeReaction?.count ?? 0;
		const dislikeCount = dislikeReaction?.count ?? 0;
		return { isLiked, isDisliked, likeCount, dislikeCount };
	}, [node.reactionCount, node.myReaction]);

	useEffect(() => {
		if (isEditing && node.attachments) {
			setFileList(node.attachments);
		}
	}, [isEditing, node.attachments]);

	const handleEdit = async () => {
		if (!onEdit) {
			onCancelEdit();
			return;
		}

		const hasContentChanged = editContent.trim() !== node.content;
		const hasAttachmentsAdded = addAttachments.length > 0;
		const hasAttachmentsRemoved = removedAttachmentIds.length > 0;

		if (!hasContentChanged && !hasAttachmentsAdded && !hasAttachmentsRemoved) {
			onCancelEdit();
			return;
		}

		setLoading(true);
		try {
			const newAttachments = addAttachments
				.filter(file => file.originFileObj)
				.map(file => file.originFileObj as File);

			await onEdit(node.id, editContent.trim(), {
				newAttachments,
				removedAttachmentIds
			});

			message.success(`Đã cập nhật ${entityType}`);
			onCancelEdit();
			setRemovedAttachmentIds([]);
			setAddAttachments([]);
		} catch (error) {
			message.error(`Không thể cập nhật ${entityType}`);
		} finally {
			setLoading(false);
		}
	};

	const handleCancelEdit = () => {
		setEditContent(node.content);
		setFileList([]);
		setAddAttachments([]);
		setRemovedAttachmentIds([]);
		onCancelEdit();
	};

	const buildMenuItems = () => {
		if (!menuOptions) return [];
		const options = menuOptions(node);
		const allOptions = [...options];

		if (node.isMine && !isDiscussion && !allOptions.find(opt => opt.key === 'edit')) {
			allOptions.unshift({
				key: 'edit',
				label: t("Chỉnh sửa", "Chỉnh sửa"),
				icon: <EditOutlined />,
				onClick: () => onStartEdit()
			});
		}

		return allOptions.map(option => ({
			key: option.key,
			label: option.label,
			icon: option.icon,
			danger: option.danger,
			onClick: () => option.onClick(node)
		}));
	};

	const menuItems = buildMenuItems();
	const [shouldShowLine, setShouldShowLine] = useState(node.children.length > 0);

	useEffect(() => {
		const observer = new MutationObserver(() => {
			const hasReplyForm = document.querySelector(`.reply-wrapper[data-parent="${node.id}"]`);
			setShouldShowLine(node.children.length > 0 || !!hasReplyForm);
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true
		});

		const hasReplyForm = document.querySelector(`.reply-wrapper[data-parent="${node.id}"]`);
		setShouldShowLine(node.children.length > 0 || !!hasReplyForm);

		return () => observer.disconnect();
	}, [node.id, node.children.length]);

	return (
		<div className={`cmt-item depth-${node.level - 1}`}>
			{shouldShowLine && <div className="vertical-line" />}

			<img
				className="cmt-avatar"
				src={node.avatar || `${process.env.PUBLIC_URL}/images/comment-avatar.png`}
				alt={node.author.fullName}
			/>

			<div className="cmt-body">
				<div className="cmt-bubble">
					<div className="flex justify-between">
						<div className="cmt-header">
							<span className="cmt-author">{node.author.fullName}</span>
							<span className="cmt-time">{dayjs(node.createdDate).fromNow()}</span>
							{isPinned && node.level === 1 && (
								<span className="cmt-pinned">
									<PushpinOutlined /> {t("Đã ghim", "Đã ghim")}
								</span>
							)}
						</div>
						<div className="flex align-center gap-8">
							{node.isMine && !isDiscussion && !isEditing && (
								<div className="flex align-center gap-16">
									<button className="cmt-btn text-primary" onClick={onStartEdit}>
										{t("Chỉnh sửa", "Chỉnh sửa")}
									</button>
									<button
										className="cmt-btn text-danger"
										onClick={() => onDelete?.(node)}
										disabled={loading}
									>
										{t("Xóa", "Xóa")}
									</button>
								</div>
							)}

							{!node.isMine && menuItems.length > 0 && (
								<Dropdown
									overlay={<Menu items={menuItems} />}
									trigger={['hover']}
									placement="bottomRight"
								>
									<Button className="cmt-btn cmt-menu-btn">
										<MoreOutlined />
									</Button>
								</Dropdown>
							)}
						</div>
					</div>

					{isEditing ? (
						<div className="cmt-edit">
							<Input.TextArea
								value={editContent}
								onChange={(e) => setEditContent(e.target.value)}
								autoSize={{ minRows: 1, maxRows: 6 }}
								onPressEnter={(e) => {
									if (!e.shiftKey) {
										e.preventDefault();
										handleEdit();
									}
								}}
							/>

							<div className="cmt-edit-upload">
								<UploadToDiscuss
									fileData={fileList}
									mode="edit"
									setAttachmentsRemoved={setRemovedAttachmentIds}
									setAddAttachments={setAddAttachments}
								/>
							</div>

							<div className="edit-actions">
								<Button size="small" onClick={handleCancelEdit}>
									{t("Hủy", "Hủy")}
								</Button>
								<Button
									size="small"
									type="primary"
									onClick={handleEdit}
									loading={loading}
								>
									{t("Lưu", "Lưu")}
								</Button>
							</div>
						</div>
					) : (
						<div className="cmt-content" dangerouslySetInnerHTML={{ __html: node.content }} />
					)}

					{!isEditing && node.attachments && node.attachments.length > 0 && (
						<div className="cmt-attachments">
							{node.attachments.some(att => att.attachmentType === 'image') && (
								<Image.PreviewGroup>
									{node.attachments
										.filter(att => att.attachmentType === 'image')
										.map(att => (
											<div key={att.id} className="cmt-attachments__item">
												<Image
													src={att.mainKey}
													alt={att.fileName}
													width={48}
													preview={{ mask: null }}
												/>
												<div className="file-name">{att.fileName}</div>
											</div>
										))}
								</Image.PreviewGroup>
							)}
							{node.attachments
								.filter(att => att.attachmentType !== 'image')
								.map(att => (
									<div key={att.id} className="cmt-file-attachment">
										<a href={att.mainKey} target="_blank" rel="noopener noreferrer">
											<span>{getFileIcon(att.attachmentType)}</span>
											<span>{att.fileName}</span>
										</a>
									</div>
								))}
						</div>
					)}
				</div>

				<div className="cmt-actions">
					<button
						className={`cmt-btn cmt-like-btn ${likeInfo.isLiked ? "liked" : ""}`}
						onClick={() => onReact?.(node.id, true)}
					>
						{likeInfo.isLiked ? <LikeActiveIcon /> : <LikeIcon />}
						<span className="like-count"> {likeInfo.likeCount} </span>
					</button>
					<button
						className={`cmt-btn cmt-dislike-btn ${likeInfo.isDisliked ? "disliked" : ""}`}
						onClick={() => onReact?.(node.id, false)}
					>
						{likeInfo.isDisliked ? <DislikeActiveIcon /> : <DislikeIcon />}
						<span className="dislike-count"> {likeInfo.dislikeCount} </span>
					</button>
					<button className="cmt-btn cmt-reply-btn" onClick={onReply}>
						<FeedbackIcon />
						{node.totalReply > 0 && (
							<span className="reply-count">{node.totalReply}</span>
						)}
						<span className="reply-txt">{t("Phản hồi", "Phản hồi")}</span>
					</button>
				</div>
			</div>
		</div>
	);
}